import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@c2fo/liquidity';
import {
  buildParticipationOffEventPayload,
  buildTurnOffParticipationParams,
  getParticipationOffEventType,
} from '@/enterprise/data/participationOffTracking';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useToggleOffer, { ToggleOfferParams } from '@/enterprise/data/useToggleOffer';
import { useOfferModalContext } from '@/enterprise/features/offerModal/OfferModalContext';
import { useReporting } from '@/reporting';
import { EnterpriseTrackEventNames } from '@/reporting/trackEventNames';
import useExchangeRates from '@/shared/data/useExchangeRates';

/**
 * Reusable hook to turn off participation for a market
 * Handles event tracking, error handling, and success feedback
 */
export const useTurnOffParticipation = (takerMarket: TakerMarket) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const { closeModal } = useOfferModalContext();
  const { mutateAsync: toggleOffer, isPending: toggleOfferIsPending } = useToggleOffer();
  const { data: exchangeRates } = useExchangeRates();

  const handleToggleSuccess = (submitValues: ToggleOfferParams, eventType: EnterpriseTrackEventNames) => {
    showSnackbar({ message: t('offerSubmitDialog.participationUpdated') });

    trackEnterpriseEvent(eventType, buildParticipationOffEventPayload({ takerMarket, submitValues, exchangeRates }));

    closeModal();
  };

  const handleToggleOffer = async (submitValues: ToggleOfferParams, eventType: EnterpriseTrackEventNames) => {
    return await toggleOffer(submitValues, {
      onSuccess: () => handleToggleSuccess(submitValues, eventType),
      onError: () => showSnackbar({ message: t('offerSubmitDialog.participationError') }),
    });
  };

  const turnOffParticipation = async () => {
    // Only proceed if participation is currently enabled
    if (!takerMarket.offerConfig.isEnabled) {
      return;
    }

    return await handleToggleOffer(
      buildTurnOffParticipationParams(takerMarket),
      getParticipationOffEventType(takerMarket)
    );
  };

  return {
    turnOffParticipation,
    isLoading: toggleOfferIsPending,
  };
};
